import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ProfileFormValues } from "@/features/users/schema";
import type {
  Department,
  ModuleRow,
  Permission,
  Role,
  UserListRow,
} from "@/features/users/types";
import { useAuth } from "@/lib/auth";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

const text = (value: string | undefined) => value?.trim() || null;

export function useCanManageUsers() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["has_permission", "users.manage", user?.id],
    enabled: isSupabaseConfigured && Boolean(user?.id),
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase.rpc("has_permission", {
        permission_code: "users.manage",
      });
      if (error) throw error;
      return Boolean(data);
    },
  });
}

export function useDepartments() {
  return useQuery({
    queryKey: ["departments"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<Department[]> => {
      const { data, error } = await supabase
        .from("departments")
        .select("id, name, code")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Department[];
    },
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ["roles"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<Role[]> => {
      const { data, error } = await supabase
        .from("roles")
        .select("id, name, code, description")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Role[];
    },
  });
}

export function useModules() {
  return useQuery({
    queryKey: ["modules"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<ModuleRow[]> => {
      const { data, error } = await supabase
        .from("modules")
        .select("id, name, code, description")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ModuleRow[];
    },
  });
}

export function usePermissions() {
  return useQuery({
    queryKey: ["permissions"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<Permission[]> => {
      const { data, error } = await supabase
        .from("permissions")
        .select("id, module_id, name, code, description, modules(code, name)")
        .order("code", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const mod = raw.modules as { code?: string; name?: string } | null;
        const { modules: _m, ...rest } = raw;
        return {
          ...(rest as Permission),
          module_code: mod?.code ?? null,
          module_name: mod?.name ?? null,
        } as Permission;
      });
    },
  });
}

export function useRolePermissionIds() {
  return useQuery({
    queryKey: ["role_permissions"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<{ role_id: string; permission_id: string }[]> => {
      const { data, error } = await supabase
        .from("role_permissions")
        .select("role_id, permission_id");
      if (error) throw error;
      return (data ?? []) as { role_id: string; permission_id: string }[];
    },
  });
}

export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<UserListRow[]> => {
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("*, departments(name)")
        .order("userid", { ascending: true });
      if (error) throw error;

      const { data: userRoles, error: urErr } = await supabase
        .from("user_roles")
        .select("user_id, role_id, roles(code, name)");
      if (urErr) throw urErr;

      const byUser = new Map<
        string,
        { ids: string[]; codes: string[]; names: string[] }
      >();
      for (const row of userRoles ?? []) {
        const raw = row as Record<string, unknown>;
        const uid = String(raw.user_id);
        const role = raw.roles as { code?: string; name?: string } | null;
        const cur = byUser.get(uid) ?? { ids: [], codes: [], names: [] };
        cur.ids.push(String(raw.role_id));
        if (role?.code) cur.codes.push(role.code);
        if (role?.name) cur.names.push(role.name);
        byUser.set(uid, cur);
      }

      return (profiles ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const dept = raw.departments as { name?: string } | null;
        const { departments: _d, ...rest } = raw;
        const roles = byUser.get(String(rest.id)) ?? { ids: [], codes: [], names: [] };
        return {
          ...(rest as Omit<UserListRow, "department_name" | "role_ids" | "role_codes" | "role_names">),
          department_name: dept?.name ?? null,
          role_ids: roles.ids,
          role_codes: roles.codes,
          role_names: roles.names,
        };
      });
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: ProfileFormValues }) => {
      const { data, error } = await supabase
        .from("profiles")
        .update({
          userid: values.userid.trim(),
          fname: values.fname.trim(),
          lname: values.lname.trim(),
          email: text(values.email),
          phone: text(values.phone),
          department_id: text(values.department_id),
          is_active: values.is_active,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Profile saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save profile"),
  });
}

export function useSetUserRoles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, roleIds }: { userId: string; roleIds: string[] }) => {
      const { data: existing, error: exErr } = await supabase
        .from("user_roles")
        .select("role_id")
        .eq("user_id", userId);
      if (exErr) throw exErr;

      const current = new Set((existing ?? []).map((r) => String(r.role_id)));
      const next = new Set(roleIds);
      const toAdd = [...next].filter((id) => !current.has(id));
      const toRemove = [...current].filter((id) => !next.has(id));

      if (toRemove.length) {
        const { error } = await supabase
          .from("user_roles")
          .delete()
          .eq("user_id", userId)
          .in("role_id", toRemove);
        if (error) throw error;
      }
      if (toAdd.length) {
        const { error } = await supabase.from("user_roles").insert(
          toAdd.map((role_id) => ({ user_id: userId, role_id })),
        );
        if (error) throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      await queryClient.invalidateQueries({ queryKey: ["has_permission"] });
      toast.success("Roles updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update roles"),
  });
}

export function useToggleRolePermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      roleId,
      permissionId,
      enabled,
    }: {
      roleId: string;
      permissionId: string;
      enabled: boolean;
    }) => {
      if (enabled) {
        const { error } = await supabase.from("role_permissions").insert({
          role_id: roleId,
          permission_id: permissionId,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("role_permissions")
          .delete()
          .eq("role_id", roleId)
          .eq("permission_id", permissionId);
        if (error) throw error;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["role_permissions"] });
      await queryClient.invalidateQueries({ queryKey: ["has_permission"] });
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update permission"),
  });
}
